// Duration helpers for time entries. Storage everywhere speaks decimal
// hours as a 2-dp string (the `time_entries.hours` NUMERIC(6,2) contract,
// e.g. "1.10" = 1h 6m), so these snap/format that string form.
//
// The 6-minute (tenth-of-an-hour) increment is a UI affordance: the stepper
// and the timer snap to it, but the API stays lenient about historical
// non-tenth rows. Keep these in one place — frontend-patterns.md §5
// forbids redefining shared formatters locally.

/** One tenth of an hour = 6 minutes, the billable increment for time entry. */
export const TENTH_HOURS = 0.1;

/** Sensible per-entry ceiling so the stepper's "+" has a stop. */
export const MAX_HOURS = 24;

/**
 * Snap a decimal-hours number to the nearest 6 minutes (tenth of an hour),
 * with the 3-minute midpoint rounding UP (1h3m → 1.1, 1h2m → 1.0).
 *
 * The rounding is done in whole minutes on purpose. The tempting
 * `Math.round(n / 0.1) * 0.1` is WRONG at the exact midpoint: in floating
 * point `0.05 / 0.1 === 0.4999…`, so `Math.round` yields 0 and 1h3m would
 * round *down*. Converting to integer minutes first sidesteps that entirely;
 * `floor((minutes + 3) / 6)` is half-up over each 6-minute block. The final
 * `toFixed(2)` neutralizes residual float drift from `tenths / 10`.
 */
export function snapToTenth(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const minutes = Math.round(n * 60); // decimal hours → whole minutes
  const tenths = Math.floor((minutes + 3) / 6); // half-up at the 3-min midpoint
  return Number((tenths / 10).toFixed(2));
}

/**
 * Snap to the nearest tenth hour, clamp to [min, max], and return the
 * canonical 2-dp string the `hours` contract expects ("0.10", "1.10").
 * Every tenth value has ≤2 decimals, so the output always satisfies the
 * API regex /^\d+(\.\d{1,2})?$/. Non-finite input falls back to `min`.
 */
export function roundToTenthHours(
  n: number,
  min = TENTH_HOURS,
  max = MAX_HOURS,
): string {
  const snapped = Number.isFinite(n) ? snapToTenth(n) : min;
  const clamped = Math.min(max, Math.max(min, snapped));
  return clamped.toFixed(2);
}

/** Milliseconds in an hour — the epoch-ms ⇄ decimal-hours conversion factor. */
export const MS_PER_HOUR = 3_600_000;

/**
 * Duration of a start→end span, snapped/clamped to the canonical `hours`
 * string. Used by <TimeRangeFields> so entering a start and an end fills in
 * the duration. A non-positive span clamps to the minimum billable tenth —
 * callers validate `end > start` and surface a field error before relying on
 * the result.
 */
export function hoursFromRange(
  startMs: number,
  endMs: number,
  min = TENTH_HOURS,
  max = MAX_HOURS,
): string {
  return roundToTenthHours((endMs - startMs) / MS_PER_HOUR, min, max);
}

/**
 * The end instant implied by a start plus a duration — the inverse of
 * `hoursFromRange`. Non-numeric hours leave the start unmoved rather than
 * producing NaN, so a half-typed stepper value can't corrupt the field.
 */
export function endFromDuration(startMs: number, hours: number | string): number {
  const n = typeof hours === 'string' ? Number(hours) : hours;
  if (!Number.isFinite(n)) return startMs;
  return Math.round(startMs + n * MS_PER_HOUR);
}

/**
 * The start instant implied by an end minus a duration — the mirror of
 * `endFromDuration`, and what <TimeRangeFields> uses when the start is the
 * calculated field ("I finished at 5:00 and worked 2.5 hours"). Non-numeric
 * hours leave the end unmoved, for the same reason: a half-typed stepper
 * value must not push NaN into the field.
 */
export function startFromDuration(endMs: number, hours: number | string): number {
  const n = typeof hours === 'string' ? Number(hours) : hours;
  if (!Number.isFinite(n)) return endMs;
  return Math.round(endMs - n * MS_PER_HOUR);
}

/**
 * Render decimal hours as "1h 6m" / "6m" / "2h". Accepts a number or a
 * numeric string (the stored `hours` form). Non-finite input → "0m".
 */
export function fmtDuration(hours: number | string): string {
  const n = typeof hours === 'string' ? Number(hours) : hours;
  if (!Number.isFinite(n)) return '0m';
  const totalMin = Math.round(n * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
